import React, { useState, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../UI/ToastProvider';
import { userService } from '../../services/userService';
import { AvatarUpload } from '../UI/AvatarUpload';
import { getAvatarUrl } from '../../utils/avatarUtils';
import { useAvatar } from '../../hooks/useAvatar';
import { avatarService } from '../../services/avatarService';
import { imageCacheService } from '../../services/imageCacheService';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose }) => {
  const { user, updateUser } = useAuth();
  const { addToast } = useToast();
  const { avatarUrl } = useAvatar({ 
    userId: user?.id, 
    avatarBase64: user?.avatarBase64 
  });

  const getRoleDisplayName = (role: string): string => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'Administrador';
      case 'manager':
        return 'Gerente';
      case 'editor':
        return 'Editor';
      case 'viewer':
        return 'Visualizador';
      case 'user':
        return 'Visitante';
      default:
        return role || 'Usuário';
    }
  };
  
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  // Atualizar nome quando user mudar
  React.useEffect(() => {
    setName(user?.name || '');
    setAvatarError(false); // Resetar erro do avatar quando usuário mudar
  }, [user]);

  // Função auxiliar para obter a URL do avatar
  const getAvatarDisplayUrl = (avatarBase64?: string): string => {
    // Usar a função utilitária para tratar o avatarBase64
    const url = getAvatarUrl(avatarBase64);
    return url;
  };

  const handleAvatarError = () => {
    console.warn('Erro ao carregar avatar do usuário:', {
      hasAvatarBase64: !!user?.avatarBase64,
      avatarBase64Length: user?.avatarBase64?.length,
      avatarError: avatarError
    });
    setAvatarError(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar senha apenas se for autenticação local e senha foi preenchida
    if (user?.authenticationType === 'local' && (currentPassword || newPassword || confirmPassword)) {
      if (newPassword !== confirmPassword) {
        addToast('As senhas não coincidem', 'error');
        return;
      }

      if (newPassword.length > 0 && newPassword.length < 6) {
        addToast('A nova senha deve ter pelo menos 6 caracteres', 'error');
        return;
      }

      if (newPassword.length > 20) {
        addToast('A nova senha deve ter no máximo 20 caracteres', 'error');
        return;
      }

      // Se preencheu nova senha, deve preencher senha atual
      if (newPassword && !currentPassword) {
        addToast('Digite sua senha atual para alterar a senha', 'error');
        return;
      }
    }

    setIsLoading(true);
    
    try {
      // Preparar FormData para envio
      const formData = new FormData();
      
      // Adicionar nome (sempre enviar o nome atual)
      formData.append('Name', name);
      
      // Adicionar senha se foi preenchida (apenas para autenticação local)
      if (user?.authenticationType === 'local') {
        if (newPassword) {
          formData.append('Password', newPassword);
        }
        
        // Se preencheu nova senha, deve incluir senha atual para validação
        if (currentPassword) {
          formData.append('CurrentPassword', currentPassword);
        }
      }
      
      // Adicionar tipo de autenticação (sempre enviar)
      formData.append('AuthenticationType', user?.authenticationType || 'local');
      
      // Debug: Log dos dados que serão enviados
      console.log('Dados do perfil a serem enviados:', {
        name,
        hasNewPassword: !!newPassword,
        hasCurrentPassword: !!currentPassword,
        authenticationType: user?.authenticationType
      });
      
      // Atualizar perfil (avatar, nome e/ou senha)
      await userService.updateProfile(formData);
      
      // Atualizar o contexto do usuário se necessário
      if (user) {
        updateUser({ ...user, name });
      }
      
      // Recarregar dados do usuário após atualização
      try {
        if (user) {
          const updatedUser = await userService.getUser(user.id);
          // Mapear apenas os campos necessários para evitar conflitos de tipo
          updateUser({
            ...user,
            name: updatedUser.name,
            avatarBase64: updatedUser.avatarBase64
          });
        }
      } catch (error) {
        console.error('Erro ao recarregar dados do usuário:', error);
      }
      
      addToast('Perfil atualizado com sucesso!', 'success');
      onClose();
      
      // Limpar formulário
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // setAvatar(null); // Removed as per edit hint
      // setAvatarFile(null); // Removed as per edit hint
      setShowPasswordFields(false);
      
    } catch (error: any) {
      console.error('Erro no handleSubmit:', error);
      addToast(error.message || 'Erro ao atualizar perfil', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Limpar formulário ao fechar
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    // setAvatar(null); // Removed as per edit hint
    // setAvatarFile(null); // Removed as per edit hint
    setName(user?.name || '');
    setShowPasswordFields(false);
    setAvatarError(false); // Limpar erro do avatar
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Atualizar Perfil
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Avatar */}
                  <div className="flex flex-col items-center space-y-4">
                    <AvatarUpload
                      userId={user?.id || ''}
                      currentAvatarUrl={avatarUrl}
                      onAvatarUpdated={async (newAvatarUrl) => {
                        // Após o upload ou remoção, atualizar o usuário no contexto
                        if (user?.id) {
                          try {
                            // Limpar cache do avatar para forçar recarregamento
                            imageCacheService.removeCachedImage(user.id);
                            
                            // Usar timestamp como versionId para cache-busting
                            const versionId = Date.now().toString();
                            
                            if (newAvatarUrl) {
                              // Avatar atualizado - usar versionId como avatarBase64
                              updateUser({
                                ...user,
                                avatarBase64: versionId
                              });
                            } else {
                              // Avatar removido - limpar completamente
                              updateUser({
                                ...user,
                                avatarBase64: undefined
                              });
                            }
                          } catch (error) {
                            console.error('Erro ao atualizar avatar:', error);
                            addToast('Avatar atualizado, mas pode não aparecer corretamente', 'warning');
                          }
                        }
                      }}
                      onAvatarRemoved={() => {
                        // Callback adicional para quando o avatar é removido
                        console.log('Avatar removido com sucesso');
                      }}
                      size="lg"
                      className="h-24 w-24"
                    />
                    <p className="text-sm text-gray-500 text-center">
                      Clique na imagem para alterar seu avatar
                    </p>
                  </div>

                  {/* Informações do usuário */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nome
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Digite seu nome"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <input
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Função
                      </label>
                      <input
                        type="text"
                        value={typeof user?.role === 'object' && user.role !== null ? (user.role as any).name : getRoleDisplayName(user?.role || '')}
                        disabled
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500"
                      />
                    </div>

                  </div>

                  {/* Alteração de senha - apenas para autenticação local */}
                  {user?.authenticationType === 'local' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        {showPasswordFields && (
                          <h4 className="text-sm font-medium text-gray-900">Alterar Senha</h4>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowPasswordFields(!showPasswordFields)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {showPasswordFields ? 'Cancelar' : 'Alterar Senha'}
                        </button>
                      </div>
                      
                      {showPasswordFields && (
                        <div className="space-y-4 border-t pt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Senha Atual
                            </label>
                            <div className="mt-1 relative">
                              <input
                                type={showCurrentPassword ? 'text' : 'password'}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Digite sua senha atual"
                                maxLength={20}
                                autoComplete="off"
                              />
                              <button
                                type="button"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              >
                                {showCurrentPassword ? (
                                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <EyeIcon className="h-5 w-5 text-gray-400" />
                                )}
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Máximo de 20 caracteres
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Nova Senha
                            </label>
                            <div className="mt-1 relative">
                              <input
                                type={showNewPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Digite a nova senha"
                                maxLength={20}
                                autoComplete="off"
                              />
                              <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              >
                                {showNewPassword ? (
                                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <EyeIcon className="h-5 w-5 text-gray-400" />
                                )}
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Mínimo de 6 e máximo de 20 caracteres
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Confirmar Nova Senha
                            </label>
                            <div className="mt-1 relative">
                              <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Confirme a nova senha"
                                maxLength={20}
                                autoComplete="off"
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              >
                                {showConfirmPassword ? (
                                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <EyeIcon className="h-5 w-5 text-gray-400" />
                                )}
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Máximo de 20 caracteres
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Informação sobre tipo de autenticação */}
                  {user?.authenticationType !== 'local' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">
                            Autenticação Externa
                          </h3>
                          <div className="mt-2 text-sm text-blue-700">
                            <p>
                              Sua conta está configurada para autenticação externa ({user?.authenticationType || 'externo'}).
                              Para alterar sua senha, entre em contato com o administrador do sistema.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botões */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UserProfileModal; 